import GeoJSON from "ol/format/GeoJSON";
import Style from "ol/style/Style";
import OlStyle from "ol/style/Style";
import { Fill, Stroke } from "ol/style";
import { toLonLat } from "ol/proj";
import { Infra3DDataProvider as Infra3DDataProvider_v3 } from "../../../api/Infra3DDataProvider_v3";
import VectorTileLayer from "ol/layer/VectorTile";
import VectorTileSource from "ol/source/VectorTile";
import { transformGeoJSON } from "../../../layer/utils";
import { project_point_on_line } from "../util";
import { distinctUntilChanged, withLatestFrom } from "rxjs";
export class RoutesLayer {
    constructor(navigator, map, routeconfigs, olstyle, olstyle_area) {
        this._layers = [];
        this._map = null;
        this._navigator = null;
        this._selected_feature = null;
        this._selectionLayers = [];
        this._map = map;
        this._navigator = navigator;
        this.init(routeconfigs, olstyle, olstyle_area);
        this._add_interactions();
    }
    get layers() {
        return this._layers;
    }
    init(routeconfigs, olstyle, olstyle_area) {
        let current_campaign = null;
        const stroke_background_routes = new Stroke({
            color: "rgba(5, 57, 82, 0.8)",
            width: 4,
        });
        const defaultstyle_background_routes = new Style({
            stroke: stroke_background_routes,
            zIndex: 200,
        });
        const stroke_background_hexes = new Fill({
            color: "rgba(5, 57, 82, 0.5)",
        });
        const defaultstyle_background_hexes = new Style({
            fill: stroke_background_hexes,
            zIndex: 200,
        });
        for (const routeconfig of routeconfigs) {
            let loaderf;
            let style = olstyle;
            if (routeconfig.type == "route_hexes")
                style = olstyle_area;
            const layer = new VectorTileLayer({
                map: this._map,
                renderMode: "vector",
                source: new VectorTileSource({
                    format: new GeoJSON(),
                    tileSize: 512,
                    overlaps: false,
                    cacheSize: 8,
                    tileLoadFunction: (tile, url) => {
                        tile.setLoader((extent, resolution, projection) => {
                            if (this._map.getView().getZoom() <= routeconfig.min_zoom ||
                                this._map.getView().getZoom() > routeconfig.max_zoom) {
                                tile.setFeatures([]);
                                return;
                            }
                            const srid = parseInt(projection.getCode().split(":").slice(-1)[0]);
                            if (!(this._navigator.api.data instanceof Infra3DDataProvider_v3)) {
                                tile.setFeatures([]);
                                return;
                            }
                            switch (routeconfig.type) {
                                case "route_lines":
                                    loaderf = this._navigator.api.data.getRouteLines({
                                        loa: routeconfig.loa,
                                        minEasting: extent[0],
                                        maxEasting: extent[2],
                                        minNorthing: extent[1],
                                        maxNorthing: extent[3],
                                        epsg: srid,
                                    });
                                    break;
                                case "route_hexes":
                                    loaderf = this._navigator.api.data.getRouteHexes({
                                        loa: routeconfig.loa,
                                        minEasting: extent[0],
                                        maxEasting: extent[2],
                                        minNorthing: extent[1],
                                        maxNorthing: extent[3],
                                        epsg: srid,
                                    });
                                    break;
                            }
                            loaderf
                                .then((geojson) => {
                                // Some features have empty coordinates, which OL does not like
                                // for the moment we remove these features, later we might want to fix the backend
                                // TODO: Remove after backend is fixed (IN-3960)
                                for (const feature of geojson.features) {
                                    if (feature.geometry.coordinates && feature.geometry.coordinates.length === 0) {
                                        feature.geometry = null;
                                    }
                                }
                                const geojson_transform = transformGeoJSON(geojson, 4326, srid);
                                const features = new GeoJSON().readFeatures(geojson_transform);
                                tile.setFeatures(features);
                            })
                                .catch((error) => {
                                tile.setFeatures([]);
                                console.error(error);
                            });
                        });
                    },
                    url: "{z}/{x}/{y}",
                }),
                zIndex: 200,
                minZoom: routeconfig.min_zoom,
                maxZoom: routeconfig.max_zoom,
                style: (feature, resolution) => {
                    if (feature.getProperties()["campaign_uids"].includes(current_campaign)) {
                        return style(feature, resolution);
                    }
                    else {
                        if (feature.getGeometry().getType() === "LineString") {
                            return defaultstyle_background_routes;
                        }
                        else {
                            return defaultstyle_background_hexes;
                        }
                    }
                },
            });
            this._layers.push(layer);
            if (routeconfig.type == "route_lines") {
                const select_style = new OlStyle({
                    stroke: new Stroke({
                        color: "rgba(255, 103, 0, 1)",
                        width: 5.0,
                    }),
                    zIndex: 210,
                });
                const selection_layer = new VectorTileLayer({
                    map: this._map,
                    renderMode: "vector",
                    source: layer.getSource(),
                    style: (feature) => {
                        if (!feature || !this._selected_feature)
                            return new Style();
                        if (!feature.getProperties() || !this._selected_feature.getProperties())
                            return new Style();
                        if (feature.getProperties().uid == this._selected_feature.getProperties().uid) {
                            return select_style;
                        }
                        return new Style();
                    },
                    zIndex: 210,
                });
                this._selectionLayers.push(selection_layer);
            }
        }
        this._navigator.stateService.currentNode$
            .pipe(withLatestFrom(this._navigator.playService.playing$), distinctUntilChanged(([x, playing_x], [y, playing_y]) => {
            if (playing_x)
                return false;
            if (!x.full)
                return false;
            return x.frame.campaignID == y.frame.campaignID;
        }))
            .subscribe(([node, playing]) => {
            if (!playing) {
                current_campaign = node.frame.campaignID;
                this._layers.forEach((layer) => {
                    layer.changed();
                });
            }
        });
    }
    _onPointerMove(evt) {
        if (evt.dragging)
            return;
        this._layers.forEach((layer) => {
            if (layer.getVisible() === false)
                return; // TODO: Check why layer.isVisible() does not work here
            if (this._map.getView().getZoom() <= layer.getMinZoom() ||
                this._map.getView().getZoom() > layer.getMaxZoom())
                return;
            layer.getFeatures(evt.pixel).then((features) => {
                if (!features.length) {
                    this._selected_feature = null;
                    this._selectionLayers.forEach((selection_layer) => {
                        selection_layer.changed();
                    });
                    // Tooltip currently deactivated, as not active in routes
                    // if (tooltip_callback) tooltip_callback(evt.coordinate, null);
                    return;
                }
                if (features[0].getGeometry().getType() !== "LineString") {
                    return;
                }
                const feature = features[0];
                if (!feature) {
                    return;
                }
                this._selected_feature = feature;
                this._selectionLayers.forEach((selection_layer) => {
                    selection_layer.changed();
                });
            });
        });
    }
    _onClick(evt) {
        if (!this._selected_feature) {
            // we also need to look for features on a click for tablet compatibility
            const features = this._map.getFeaturesAtPixel(evt.pixel, {
                hitTolerance: 20, // in css pixel
                layerFilter: (layer) => {
                    if (!(layer instanceof VectorTileLayer))
                        return false;
                    if (!layer.isVisible())
                        return false;
                    return this._layers.includes(layer);
                },
            });
            if (features.length === 0) {
                return;
            }
            this._selected_feature = features[0]; // always pick the first feature -> hypothesis: getFeaturesAtPixel seems to return the closest feature first
            this._selectionLayers.forEach((selection_layer) => {
                selection_layer.changed();
            });
        }
        const coordinate = project_point_on_line(evt.coordinate, this._selected_feature);
        const lonlat = toLonLat(coordinate);
        const latlon = {
            lat: lonlat[1],
            lon: lonlat[0],
        };
        this._map.getTargetElement().style.cursor = "wait";
        this._navigator.movePosition$(latlon, undefined, this._selected_feature.getProperties().sequence_id).subscribe((node) => {
            this._map.getTargetElement().style.cursor = "auto";
            // Reset selection after change
            this._selected_feature = null;
            this._selectionLayers.forEach((selection_layer) => {
                selection_layer.changed();
            });
        }, (error) => {
            this._map.getTargetElement().style.cursor = "not-allowed";
            setTimeout(() => {
                this._map.getTargetElement().style.cursor = "auto";
            }, 500);
            // Reset selection after change
            this._selected_feature = null;
            this._selectionLayers.forEach((selection_layer) => {
                selection_layer.changed();
            });
        });
    }
    _add_interactions() {
        this._map.on("pointermove", (evt) => this._onPointerMove(evt));
        this._map.on("click", (evt) => this._onClick(evt));
    }
    _remove_interactions() {
        this._map.un("pointermove", (evt) => this._onPointerMove(evt));
        this._map.un("click", (evt) => this._onClick(evt));
    }
    setVisible(visible) {
        this.layers.forEach((layer) => {
            layer.setVisible(visible);
            layer.changed();
        });
        if (visible) {
            this._add_interactions();
        }
        else {
            this._remove_interactions();
        }
    }
}
//# sourceMappingURL=RoutesLayer.js.map